import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ExplanationPointResponse } from '@api-response-types/explanation-point.response';
import { UpdatedForecastSeriesResponse } from '@api-response-types/updated-forecast-series.response';
import {
  Resolution,
  toValue as toResolutionValue,
} from '@api-types/enums/resolution.enum';
import { convertFiltersMapToRecord } from '@api-types/helper/helper';
import { FilterValue } from '@api-types/types/filter-value.type';
import { PmIconComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  endOfISOWeek,
  endOfMonth,
  endOfYear,
  format,
  isSameDay,
  isSameISOWeek,
  isSameMonth,
  isSameYear,
  parseISO,
  startOfISOWeek,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { FeatureTranslationService } from 'demand-forecasting/services/feature-translation.service';
import { ForecastSeriesService } from 'demand-forecasting/services/forecast-series/forecast-series.service';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectAllFilterCombinationForecastSeries,
  selectAppliedFiltersAsRecord,
  selectFilterCombinationNameById,
  selectSavedFilterCombinations,
} from 'demand-forecasting/store/filter/filter.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  selectResolution,
  selectTargetUnit,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ExplanationPoint } from 'demand-forecasting/types/explanation-point.type';
import ExplanationChartOptions from 'demand-forecasting/types/forecast-chart/explanation-chart-options';
import { ForecastDateKpi } from 'demand-forecasting/types/forecast-chart/forecast-date-kpi.interface';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts, { XAxisOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideMousePointerClick } from '@lucide/angular';
import { ClickedPredictionPoint } from '../forecast-chart/forecast-chart.component';
import { ForecastPredictionKpisComponent } from '../forecast-prediction-kpis/forecast-prediction-kpis.component';

type PredictionExplanation = {
  color: string;
  features: { key: string; value: number }[];
};

type SeriesObject = {
  data: Array<number | null>;
  color: string;
};

@Component({
  selector: 'pm-forecast-prediction-explanation',
  templateUrl: './forecast-prediction-explanation.component.html',
  styleUrls: ['./forecast-prediction-explanation.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    ForecastPredictionKpisComponent,
    HighchartsChartComponent,
    MatCard,
    MatCardContent,
    TranslateModule,
    DatePipe,
    PmIconComponent,
    MatProgressSpinner,
  ],
})
export class ForecastPredictionExplanationComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslateService);
  private readonly forecastsHttpClient = inject(ForecastsHttpClient);
  @ViewChild('explanationPanel') explanationPanel!: MatExpansionPanel;

  configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );
  forecastSeries = this.store.selectSignal(latestCompletedForecastSeries);
  targetUnit = this.store.selectSignal(selectTargetUnit);
  filterCombinationSeries = this.store.selectSignal(
    selectAllFilterCombinationForecastSeries,
  );
  factorColumn = this.store.selectSignal<string | null | undefined>(
    ConfigurationsSelectors.selectFactorColumnByUrlParam,
  );
  appliedFiltersRecord = this.store.selectSignal(selectAppliedFiltersAsRecord);
  savedFilterCombinations = this.store.selectSignal(
    selectSavedFilterCombinations,
  );
  resolution = this.store.selectSignal(selectResolution);
  forecastVersion = this.store.selectSignal(selectForecastVersionByQueryParam);

  private readonly clickedPredictionPointSignal = signal<
    ClickedPredictionPoint | undefined
  >(undefined);

  @Input({ required: true })
  set clickedPredictionPoint(value: ClickedPredictionPoint | undefined) {
    this.clickedPredictionPointSignal.set(value);
  }
  get clickedPredictionPoint(): ClickedPredictionPoint | undefined {
    return this.clickedPredictionPointSignal();
  }

  protected translateService = inject(TranslateService);
  protected featureTranslationService = inject(FeatureTranslationService);
  private readonly forecastSeriesService = inject(ForecastSeriesService);

  protected readonly clickIcon = LucideMousePointerClick;
  predictionExplanations: PredictionExplanation[] = [];
  chartOptions: HighchartsOptions = BaseChartOptions.getEmptyChartOptions();
  chartUpdated = false;
  allPlainCategories: string[] = [];
  forecastDateKpis: ForecastDateKpi[] = [];
  baseValue: number | null = null;
  maxBarsToDisplay = 10;
  protected readonly loadingSpinnerDiameter = 50;
  private loadedExplanationData: UpdatedForecastSeriesResponse | null = null;
  private readonly predictionExplanationRequest = computed(() => {
    const clickedPoint = this.clickedPredictionPointSignal();
    const version = this.forecastVersion();
    const resolutionParam = this.resolution();
    const resolution = resolutionParam
      ? toResolutionValue(resolutionParam)
      : undefined;
    const filters = this.getMergedFilters();
    const factorColumn = this.factorColumn();

    if (!clickedPoint?.date || !version || !resolution) {
      return undefined;
    }

    const clickedDate = new Date(clickedPoint.date);
    const { minDate, maxDate } = this.getDateRange(clickedDate, resolution);
    const dateForLookup = resolution === Resolution.Daily ? minDate : maxDate;

    return {
      forecastId: version.forecastId,
      date: dateForLookup,
      resolution,
      minDate,
      maxDate,
      factorColumn,
      filters,
    };
  });

  predictionExplanationResource =
    this.forecastsHttpClient.predictionExplanationResource(() =>
      this.predictionExplanationRequest(),
    );

  dataSeries = computed(() => {
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      return undefined;
    }
    return this.forecastSeriesService.toSeriesData(forecastSeries);
  });

  ngOnInit(): void {
    this.chartOptions = ExplanationChartOptions.getChartOptions();
    this.createChartSeriesAndUpdate();
    this.updateForecastDateKpis();
  }
  private readonly predictionExplanationEffect = effect(() => {
    const request = this.predictionExplanationRequest();
    const explanation = this.predictionExplanationResource.value();

    if (!request) {
      this.resetExplanationState();
      return;
    }

    this.loadedExplanationData = null;
    this.predictionExplanations = [];
    this.forecastDateKpis = [];

    if (!explanation) {
      this.chartOptions.series = [];
      return;
    }

    const baseForecast = this.forecastSeries();
    if (!baseForecast) return;

    const clickedTimestamp = parseISO(request.date);
    const explanationIndex = this.findIndexForClickedDate(
      baseForecast.originalDates,
      clickedTimestamp,
    );

    if (explanationIndex === -1) {
      return;
    }

    const updatedExplanations = [...(baseForecast.explanations ?? [])];
    updatedExplanations[explanationIndex] = explanation.explanation as Record<
      string,
      number | ExplanationPointResponse
    >;

    this.loadedExplanationData = {
      ...baseForecast,
      explanations: updatedExplanations,
    } as unknown as UpdatedForecastSeriesResponse;

    this.createChartSeriesAndUpdate();
    this.updateForecastDateKpis();
    if (this.explanationPanel && !this.explanationPanel.expanded) {
      this.explanationPanel.open();
    }
  });

  private getDateRange(
    clickedDate: Date,
    resolution: Resolution,
  ): {
    minDate: string;
    maxDate: string;
  } {
    if (resolution === Resolution.Daily) {
      return {
        minDate: format(clickedDate, 'yyyy-MM-dd'),
        maxDate: format(clickedDate, 'yyyy-MM-dd'),
      };
    }
    if (resolution === Resolution.Weekly) {
      return {
        minDate: format(startOfISOWeek(clickedDate), 'yyyy-MM-dd'),
        maxDate: format(endOfISOWeek(clickedDate), 'yyyy-MM-dd'),
      };
    }
    if (resolution === Resolution.Monthly) {
      return {
        minDate: format(startOfMonth(clickedDate), 'yyyy-MM-dd'),
        maxDate: format(endOfMonth(clickedDate), 'yyyy-MM-dd'),
      };
    }
    return {
      minDate: format(startOfYear(clickedDate), 'yyyy-MM-dd'),
      maxDate: format(endOfYear(clickedDate), 'yyyy-MM-dd'),
    };
  }

  private resetExplanationState(): void {
    this.loadedExplanationData = null;
    this.predictionExplanations = [];
    this.forecastDateKpis = [];
    this.chartOptions.series = [];
    this.chartUpdated = true;
    this.baseValue = null;
    this.allPlainCategories = [];
  }

  private getMergedFilters(): Record<string, FilterValue[]> {
    const appliedFilters = this.appliedFiltersRecord() || {};

    if (this.clickedPredictionPoint?.filterCombinationId) {
      const savedFilterCombinations = this.savedFilterCombinations();
      const filterCombination = savedFilterCombinations.find(
        (fc) => fc.id === this.clickedPredictionPoint?.filterCombinationId,
      );
      const quickFilters = filterCombination
        ? convertFiltersMapToRecord(
            new Map(Object.entries(filterCombination.appliedFilters)),
          )
        : {};

      return {
        ...appliedFilters,
        ...quickFilters,
      };
    }

    return appliedFilters;
  }
  private findIndexForClickedDate(
    originalDates: [number, unknown][],
    clickedDate: Date,
  ): number {
    const resolution = this.resolution();
    const clicked = clickedDate;

    return originalDates.findIndex(([ts]) => {
      const d = new Date(ts);

      switch (resolution) {
        case Resolution.Daily:
          return isSameDay(d, clicked);
        case Resolution.Weekly:
          return isSameISOWeek(d, clicked);
        case Resolution.Monthly:
          return isSameMonth(d, clicked);
        case Resolution.Yearly:
          return isSameYear(d, clicked);
        default:
          return false;
      }
    });
  }

  setXAxisTitle(): void {
    const axisTitle = {
      text: this.translateService.instant('RESULT.EXPLANATION.X_AXIS'),
    };
    this.chartOptions.xAxis.title = axisTitle;
  }

  setYAxisTitle(): void {
    const axisTitle = {
      text: this.translateService.instant('RESULT.EXPLANATION.Y_AXIS', {
        targetUnit:
          this.targetUnit() || this.configuration()?.startForecastConfig.target,
      }),
    };
    this.chartOptions.yAxis.title = axisTitle;
  }

  updateForecastDateKpis(): void {
    this.forecastDateKpis = [];

    if (this.clickedPredictionPoint?.date) {
      const dataSeries = this.getCurrentSeriesProps();
      if (!dataSeries) {
        return;
      }
      this.forecastDateKpis.push({
        color: this.clickedPredictionPoint.color,
        filterName: this.translateService.instant(
          'RESULT.PREDICTION.FILTERNAME',
        ),
        baseValue: this.extractBaseline(
          dataSeries.originalDates,
          dataSeries.explanations ?? [],
          this.clickedPredictionPoint?.date,
        ),
        value:
          dataSeries.values.data.find(
            (value) => value[0] === this.clickedPredictionPoint?.date,
          )?.[1] || 0,
        prediction:
          dataSeries?.predictions?.data.find(
            (prediction) => prediction[0] === this.clickedPredictionPoint?.date,
          )?.[1] || 0,
      });
    }
  }

  createChartSeriesAndUpdate(): void {
    if (!this.clickedPredictionPoint?.date) {
      return;
    }
    this.predictionExplanations = [];
    this.chartOptions.series = [];
    this.setChartCategories(this.clickedPredictionPoint?.date);

    const dataSeries = this.getCurrentSeriesProps();
    if (dataSeries && this.clickedPredictionPoint?.date) {
      this.extractFeatureImportance(
        dataSeries.predictions,
        dataSeries.explanations ?? [],
        this.clickedPredictionPoint?.date,
      );
    }

    this.predictionExplanations.forEach((predictionExplanation) => {
      const seriesObject: SeriesObject = {
        data: [],
        color: predictionExplanation.color,
      };
      this.allPlainCategories.forEach((categoryFeature) => {
        seriesObject.data.push(
          predictionExplanation.features.find(
            (feature) => feature.key === categoryFeature,
          )?.value ?? null,
        );
      });
      this.chartOptions.series.push({
        name: this.clickedPredictionPoint?.filterCombinationId
          ? this.store.selectSignal(
              selectFilterCombinationNameById(
                this.clickedPredictionPoint?.filterCombinationId,
              ),
            )()
          : this.translationService.instant('RESULT.CHART.PREDICTION'),

        data: seriesObject.data,
        type: 'bar',
        color: this.clickedPredictionPoint?.color,
      });
    });
    this.baseValue = this.removeBaselineValue();
    this.setValueRangeOfBars();
    this.chartOptions = ExplanationChartOptions.patchAmountOfBarsToDisplay(
      this.chartOptions,
      this.maxBarsToDisplay,
    );
    this.setXAxisTitle();
    this.setYAxisTitle();
    this.chartOptions.legend = { enabled: false };
    this.chartUpdated = true;
  }

  setChartCategories(clickedDate: number): void {
    (this.chartOptions.xAxis as XAxisOptions).categories = [];
    this.allPlainCategories = [];
    const dataSeries = this.getCurrentSeriesProps();
    if (!dataSeries) {
      return;
    }
    const indexOfClickedDate = this.findIndexForClickedDate(
      dataSeries.originalDates,
      new Date(clickedDate),
    );
    if (indexOfClickedDate === -1) {
      return;
    }
    const explanation = (dataSeries.explanations ?? [])[indexOfClickedDate];
    if (!explanation) {
      return;
    }
    this.allPlainCategories = Object.entries(explanation)
      .sort(
        ([, valueA], [, valueB]) =>
          Math.abs((valueB as ExplanationPoint).contribution) -
          Math.abs((valueA as ExplanationPoint).contribution),
      )
      .map(([key]) => key);

    const forecastSeries = this.forecastSeries();
    if (forecastSeries) {
      (this.chartOptions.xAxis as XAxisOptions).categories =
        this.featureTranslationService.translateFeatures(
          this.allPlainCategories,
        );
    }
  }

  extractFeatureImportance(
    predictions: UpdatedForecastSeriesResponse['predictions'],
    explanations: UpdatedForecastSeriesResponse['explanations'] | [],
    clickedDate: number,
  ): void {
    if (!clickedDate) {
      return;
    }
    const predictionExplanation: {
      color: string;
      features: { key: string; value: number }[];
    } = {
      features: [],
      color: this.clickedPredictionPoint?.color || '',
    };

    const dataSeries = this.getCurrentSeriesProps();
    if (!dataSeries) {
      return;
    }

    const indexOfClickedDate = this.findIndexForClickedDate(
      dataSeries.originalDates,
      new Date(clickedDate),
    );
    if (indexOfClickedDate === -1) {
      return;
    }
    const explanation = explanations?.[indexOfClickedDate];

    if (
      explanation &&
      Object.keys(explanation).some((key) => explanation[key] !== null)
    ) {
      Object.keys(explanation).forEach((key) => {
        // For actuals there is no prediction explanation
        if (explanation[key] !== null) {
          const explanationKey = explanation[key];
          predictionExplanation.features.push({
            key,
            value: this.transformExplanationIntoNumber(explanationKey),
          });
        }
      });
    }

    if (predictionExplanation.features.length) {
      this.predictionExplanations.push(predictionExplanation);
    }
  }

  /**
   * Sets the min and max value of the y axis to the min and max value of the data. This is necessary to avoid automatically zooming when scrolling the chart
   */
  private setValueRangeOfBars(): void {
    const series = this.chartOptions.series as Highcharts.SeriesBarOptions[];
    const min = Math.min(
      ...series.map((entry) => Math.min(...(entry.data as number[]))),
    );
    const max = Math.max(
      ...series.map((entry) => Math.max(...(entry.data as number[]))),
    );
    (this.chartOptions.yAxis as Highcharts.YAxisOptions).min = min;
    (this.chartOptions.yAxis as Highcharts.YAxisOptions).max = max;
  }

  private removeBaselineValue(): number | null {
    const xAxisCategories = (this.chartOptions.xAxis as XAxisOptions)
      .categories;
    if (!xAxisCategories) return null;
    const seriesOptions = this.chartOptions
      .series as Highcharts.SeriesBarOptions[];
    const baselineIndex = xAxisCategories.findIndex(
      (category) =>
        category ===
        this.translateService.instant('RESULT.EXPLANATION.Y_FIELD._Baseline'),
    );
    if (baselineIndex !== -1) {
      xAxisCategories.splice(baselineIndex, 1);
      return (
        (seriesOptions[0]?.data?.splice(baselineIndex, 1)[0] as number) || null
      );
    }
    return null;
  }

  /**
   * Extracts the baseline value from the dataSeries for the specified date
   * @param dataSeries
   * @param indexOfClickedDate
   * @returns
   */
  private extractBaseline(
    originalDates: UpdatedForecastSeriesResponse['originalDates'],
    explanations: UpdatedForecastSeriesResponse['explanations'] | [],
    clickedDate: number,
  ): number | null {
    const baselineIndex = this.findIndexForClickedDate(
      originalDates,
      new Date(clickedDate),
    );
    if (baselineIndex === -1) return null;

    return (
      ((explanations ?? [])[baselineIndex]?._Baseline as ExplanationPoint)
        ?.contribution ?? null
    );
  }

  private transformExplanationIntoNumber(
    feature: number | ExplanationPointResponse,
  ): number {
    const isNumber = typeof feature === 'number';
    return isNumber ? feature : feature?.contribution;
  }

  private getCurrentSeriesProps():
    | undefined
    | {
        originalDates: UpdatedForecastSeriesResponse['originalDates'];
        explanations: UpdatedForecastSeriesResponse['explanations'] | [];
        values: UpdatedForecastSeriesResponse['values'];
        predictions: UpdatedForecastSeriesResponse['predictions'];
      } {
    if (this.clickedPredictionPoint === undefined) {
      return undefined;
    }

    if (this.loadedExplanationData) {
      return {
        originalDates: this.loadedExplanationData.originalDates,
        explanations: this.loadedExplanationData.explanations,
        values: this.loadedExplanationData.values,
        predictions: this.loadedExplanationData.predictions,
      };
    }

    // determine if the clicked prediction point is a filter combination or a standard forecast

    if (this.clickedPredictionPoint.filterCombinationId) {
      const filterCombinationSeries = this.filterCombinationSeries();
      const res =
        filterCombinationSeries[
          this.clickedPredictionPoint.filterCombinationId
        ];
      return {
        originalDates: res.originalDates,
        explanations: res.explanations,
        values: res.values,
        predictions: res.predictions,
      };
    }
    const forecastSeries = this.forecastSeries();
    if (forecastSeries) {
      return {
        originalDates: forecastSeries.originalDates,
        explanations: forecastSeries.explanations,
        values: forecastSeries.values,
        predictions: forecastSeries.predictions,
      };
    }
    return undefined;
  }
  get isLoadingExplanation(): boolean {
    return this.predictionExplanationResource.isLoading();
  }
}
